import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { API_SERVICE } from "../Services/CommonApi";
import {
  CLEAR_NOTIFICATION_PREFERENCES,
  GET_NOTIFICATION_SETTINGS,
  SAVE_NOTIFICATION_CHANNEL,
  SAVE_NOTIFICATION_PREFERENCES,
} from "../constant/CommonApiUrl";
import {
  CHANNELS,
  applyToggle,
  findNotification,
  hasSettingsShape,
  normalizeSettings,
  preferencePayload,
} from "../helper/notificationSettings";

// The settings endpoints need their tables, and answer 500 until step 7 of
// Deploy-Notification.ps1 has been applied to the tenant. That is a screen-level
// retry, not a per-toggle failure, so it is the load that reports it.
export const fetchNotificationSettings = createAsyncThunk(
  "notificationSettings/fetch",
  async (_arg, thunkAPI) => {
    try {
      const response = await API_SERVICE.get(GET_NOTIFICATION_SETTINGS);
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const saveNotificationPreference = createAsyncThunk(
  "notificationSettings/savePreference",
  async ({ eventTypeCode, channel, nextValue }, thunkAPI) => {
    const { modules } = thunkAPI.getState().root.notificationSettings;
    const notification = findNotification(modules, eventTypeCode);

    if (!notification) return thunkAPI.rejectWithValue("Unknown notification");

    try {
      const response = await API_SERVICE.post(SAVE_NOTIFICATION_PREFERENCES, {
        preferences: [preferencePayload(notification, channel, nextValue)],
      });
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const saveNotificationChannel = createAsyncThunk(
  "notificationSettings/saveChannel",
  async ({ channel, enabled }, thunkAPI) => {
    try {
      const response = await API_SERVICE.post(SAVE_NOTIFICATION_CHANNEL, {
        channel,
        enabled,
      });
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

// Restores catalog defaults for one channel. It does not switch everything off,
// and it deliberately leaves the master channel switch alone.
export const clearNotificationPreferences = createAsyncThunk(
  "notificationSettings/clear",
  async ({ channel }, thunkAPI) => {
    try {
      const response = await API_SERVICE.post(CLEAR_NOTIFICATION_PREFERENCES, {
        channel,
      });
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

// Both tabs are mounted at once and the same event type appears in each, so
// in-flight state has to name the channel too. Keyed on the type alone, saving an
// email toggle would disable that row's browser switch.
export const savingKey = (channel, eventTypeCode) =>
  `${channel}:${eventTypeCode}`;

const initialState = {
  channels: { email: false, browser: false },
  modules: [],
  loadStatus: "idle", // idle | loading | loaded | error
  savingTypes: [], // "<channel>:<eventTypeCode>" for each save in flight
  savingChannel: null, // "email" | "browser" while the master switch is saving
  clearingChannel: null, // "email" | "browser" while that tab is being reset
  // Each in-flight toggle: what it looked like before it moved, so a failed save
  // can put it back, and where it was moving to, so a response for a different
  // type does not drag it backwards on its way past.
  pending: {},
};

// Every write answers with the state that actually landed — SQL drops toggles for
// modules the caller's role cannot see — so the tree is always taken from the
// response rather than from what we optimistically drew.
const applySettings = (state, payload) => {
  const settings = payload?.data?.settings ?? payload?.data;
  if (!hasSettingsShape(settings)) return;

  const normalized = normalizeSettings(settings);
  state.channels = normalized.channels;
  state.modules = normalized.modules;

  // The tree the server sends knows nothing about the toggles still in flight, so
  // redraw them on top of it. Without this, flipping four switches quickly makes
  // the later three snap back as the first response lands.
  state.modules = Object.values(state.pending).reduce(
    (modules, entry) =>
      applyToggle(modules, entry.eventTypeCode, entry.channel, entry.nextValue),
    state.modules,
  );
};

const notificationSettingsSlice = createSlice({
  name: "notificationSettings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationSettings.pending, (state) => {
        state.loadStatus = "loading";
      })
      .addCase(fetchNotificationSettings.fulfilled, (state, { payload }) => {
        state.loadStatus = "loaded";
        // "204" is an empty screen (no modules for this role), not an error.
        applySettings(state, payload);
      })
      .addCase(fetchNotificationSettings.rejected, (state) => {
        state.loadStatus = "error";
      })

      .addCase(saveNotificationPreference.pending, (state, { meta }) => {
        const { eventTypeCode, channel, nextValue } = meta.arg;
        const notification = findNotification(state.modules, eventTypeCode);
        if (!notification) return;

        state.pending[savingKey(channel, eventTypeCode)] = {
          eventTypeCode,
          channel,
          nextValue,
          value: notification[CHANNELS[channel].enabledField],
          isUserSet: notification.isUserSet,
        };
        state.savingTypes.push(savingKey(channel, eventTypeCode));
        state.modules = applyToggle(
          state.modules,
          eventTypeCode,
          channel,
          nextValue,
        );
      })
      .addCase(
        saveNotificationPreference.fulfilled,
        (state, { payload, meta }) => {
          const { eventTypeCode, channel } = meta.arg;
          const key = savingKey(channel, eventTypeCode);

          state.savingTypes = state.savingTypes.filter((code) => code !== key);
          // Dropped before applySettings, so this type takes the server's value
          // while every other in-flight toggle is redrawn on top of it.
          delete state.pending[key];
          applySettings(state, payload);

          // saved < submitted is still a success: the toggle was dropped because
          // the caller's role cannot see that module. Saying nothing would leave
          // the switch looking as though it moved.
          const { submitted, saved } = payload?.data || {};
          if (Number.isFinite(saved) && saved < submitted) {
            toast.warning(
              payload?.message ||
                "Some preferences could not be saved for your role.",
            );
          }
        },
      )
      .addCase(saveNotificationPreference.rejected, (state, { meta }) => {
        const { eventTypeCode, channel } = meta.arg;
        const key = savingKey(channel, eventTypeCode);
        const previous = state.pending[key];

        state.savingTypes = state.savingTypes.filter((code) => code !== key);

        if (previous) {
          state.modules = applyToggle(
            state.modules,
            eventTypeCode,
            previous.channel,
            previous.value,
          );
          const restored = findNotification(state.modules, eventTypeCode);
          if (restored) restored.isUserSet = previous.isUserSet;
          delete state.pending[key];
        }

        toast.error("Couldn't save that preference. Please try again.");
      })

      .addCase(saveNotificationChannel.pending, (state, { meta }) => {
        state.savingChannel = meta.arg.channel;
        state.channels[meta.arg.channel] = meta.arg.enabled;
      })
      .addCase(saveNotificationChannel.fulfilled, (state, { payload }) => {
        state.savingChannel = null;
        const channels = payload?.data?.channels;
        if (channels) {
          state.channels = normalizeSettings({ channels }).channels;
        }
      })
      .addCase(saveNotificationChannel.rejected, (state, { meta }) => {
        state.savingChannel = null;
        state.channels[meta.arg.channel] = !meta.arg.enabled;
        toast.error("Couldn't change that setting. Please try again.");
      })

      .addCase(clearNotificationPreferences.pending, (state, { meta }) => {
        state.clearingChannel = meta.arg.channel;
      })
      .addCase(clearNotificationPreferences.fulfilled, (state, { payload }) => {
        state.clearingChannel = null;
        applySettings(state, payload);
        toast.success("Preferences reset to their defaults.");
      })
      .addCase(clearNotificationPreferences.rejected, (state) => {
        state.clearingChannel = null;
        toast.error("Couldn't reset your preferences. Please try again.");
      });
  },
});

export default notificationSettingsSlice.reducer;
